import {useMemo} from 'react'
import {
  type AccessibilityProps,
  type TextStyle,
  View,
  type ViewStyle,
} from 'react-native'

import {atoms as a, native, useTheme} from '#/alf'
import * as Toggle from '#/components/forms/Toggle'
import {Text} from '#/components/Typography'
import {useSemanticTokens} from '#/wsocial/service/design-system'

type ItemProps = Omit<Toggle.ItemProps, 'style' | 'role' | 'children'> &
  AccessibilityProps & {
    children: React.ReactElement<any>
    testID?: string
  }

export type GroupProps = Omit<Toggle.GroupProps, 'style' | 'type'> & {
  multiple?: boolean
}

/**
 * @deprecated - use SegmentedControl
 */
export function Group({children, multiple, ...props}: GroupProps) {
  const s = useSemanticTokens()
  return (
    <Toggle.Group type={multiple ? 'checkbox' : 'radio'} {...props}>
      <View
        style={[
          a.w_full,
          a.flex_row,
          a.rounded_sm,
          a.overflow_hidden,
          {borderColor: s.border.default},
          {borderWidth: 1},
        ]}>
        {children}
      </View>
    </Toggle.Group>
  )
}

/**
 * @deprecated - use SegmentedControl
 */
export function Button({children, ...props}: ItemProps) {
  return (
    <Toggle.Item {...props} style={[a.flex_grow, a.flex_1]}>
      <ButtonInner>{children}</ButtonInner>
    </Toggle.Item>
  )
}

function ButtonInner({children}: React.PropsWithChildren<{}>) {
  const t = useTheme()
  const s = useSemanticTokens()
  const state = Toggle.useItemContext()

  const {baseStyles, hoverStyles, activeStyles} = useMemo(() => {
    const base: ViewStyle[] = []
    const hover: ViewStyle[] = []
    const active: ViewStyle[] = []

    hover.push(
      t.name === 'light'
        ? {backgroundColor: s.bg.contrast_100}
        : {backgroundColor: s.bg.contrast_25},
    )

    if (state.selected) {
      active.push({
        backgroundColor: s.toggleButton.selectedBgHover,
      })
      hover.push({
        backgroundColor: s.toggleButton.selectedBgHover,
      })

      if (state.disabled) {
        active.push({
          backgroundColor: s.toggleButton.selectedBgDisabled,
        })
      }
    }

    if (state.disabled) {
      base.push({
        backgroundColor: s.toggleButton.disabledBg,
      })
    }

    return {
      baseStyles: base,
      hoverStyles: hover,
      activeStyles: active,
    }
  }, [t, s, state])

  return (
    <View
      style={[
        {
          borderLeftWidth: 1,
          marginLeft: -1,
        },
        a.flex_grow,
        a.py_md,
        native({
          paddingBottom: 10,
        }),
        a.px_md,
        {backgroundColor: s.bg.default},
        {borderColor: s.border.default},
        baseStyles,
        activeStyles,
        (state.hovered || state.pressed) && hoverStyles,
      ]}>
      {children}
    </View>
  )
}

/**
 * @deprecated - use SegmentedControl
 */
export function ButtonText({children}: {children: React.ReactNode}) {
  const s = useSemanticTokens()
  const state = Toggle.useItemContext()

  const textStyles = useMemo(() => {
    const text: TextStyle[] = []
    if (state.selected) {
      text.push({color: s.text.inverted})
    }
    if (state.disabled) {
      text.push({
        opacity: 0.5,
      })
    }
    return text
  }, [s, state])

  return (
    <Text
      style={[
        a.text_center,
        a.font_semi_bold,
        {color: s.text.medium},
        textStyles,
      ]}>
      {children}
    </Text>
  )
}
